import { SelfContainedCommand, CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../../utils/logger';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

const log = createFileLogger('NOTES_COMMANDS');

export const notesCreateCommand: SelfContainedCommand = {
    id: 'notes_create',
    description: '📝 CREATE: Create a new note entity and optionally open it in a window. By default creates a window unless createWindow=false is passed.',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',
    
    schema: {
        parameters: [
            {
                name: 'title',
                type: 'string',
                description: 'Title for the new note (default: "New Note")',
                required: false,
                defaultValue: 'New Note',
                validator: (value: string) => {
                    if (value && value.trim().length === 0) return 'Note title cannot be empty';
                    if (value && value.length > 100) return 'Note title too long (max 100 chars)';
                    return null;
                },
                examples: ['Meeting Notes', 'Research Ideas', 'Project Plan']
            },
            {
                name: 'spaceId',
                type: 'string',
                description: 'The ID of the space to create the note in. Will use context spaceId if not provided.',
                required: false,
                examples: ['space-123', 'space-abc-def']
            },
            {
                name: 'folderId',
                type: 'string',
                description: 'The ID of the folder to create the note in. Leave empty for root level.',
                required: false,
                examples: ['folder-123', null]
            },
            {
                name: 'createWindow',
                type: 'boolean',
                description: 'Whether to create and open a window for the note. Default is true.',
                required: false,
                defaultValue: true,
                examples: [true, false]
            }
        ]
    },
    
    examples: {
        good: [
            {
                input: { title: 'Research Notes' },
                expectedOutput: createSuccessResult('Note created successfully', {
                    note: {
                        id: 'note-123',
                        title: 'Research Notes',
                        entityType: 'Note'
                    },
                    window: {
                        id: 'window-123',
                        title: 'Research Notes',
                        appType: 'notes-markdown'
                    }
                }),
                description: 'Create a new note with a custom title and window',
                context: 'When starting a new note for a specific purpose'
            },
            {
                input: { title: 'Meeting Notes', spaceId: 'space-xyz' },
                expectedOutput: createSuccessResult('Note created successfully', {
                    note: {
                        id: 'note-456',
                        title: 'Meeting Notes',
                        entityType: 'Note'
                    },
                    window: {
                        id: 'window-456',
                        title: 'Meeting Notes',
                        appType: 'notes-markdown'
                    }
                }),
                description: 'Create a new note in a specific space',
                context: 'When organizing notes across different spaces'
            }
        ],
        bad: [
            {
                input: { title: '' },
                error: 'Note title cannot be empty',
                why: 'Empty titles are not allowed',
                fix: 'Provide a non-empty title or omit the parameter to use default'
            }
        ]
    },
    
    performance: {
        expectedDuration: 500,
        complexity: 'low'
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
        const startTime = Date.now();
        const { title = 'New Note', spaceId: providedSpaceId, folderId, createWindow = true } = params;
        const spaceId = providedSpaceId || context.spaceId;
        
        try {
            log.log('[notes_create] Creating new note:', { title, spaceId });
            
            // ✅ ARCHITECTURE FIX: Use entityActions instead of direct entityService
            // This ensures proper event emission, store updates, and CRDT sync
            const now = new Date().toISOString();
            const newNoteEntityData = {
                entityType: 'Note',
                name: title.trim(),
                spaceId: spaceId,
                skeleton: {
                    '@type': 'Note',
                    version: 'v2', // ✅ NEW: Create v2 notes
                    architecture: 'unified', // ✅ NEW: Use unified architecture
                    preview: '',
                    createdAt: now,
                    updatedAt: now,
                    groupIds: folderId ? [folderId] : [] // Set groupIds during creation
                },
                created_at: now,
            };
            
            log.log('[notes_create] Creating note via entityActions:', { 
                spaceId: newNoteEntityData.spaceId,
                title: newNoteEntityData.name,
                folderId
            });
            
            // ✅ Use store actions - handles EVERYTHING automatically:
            // - Backend creation
            // - Store updates via _applyLocalUpsert
            // - CRDT sync
            // - Event emission
            // - Normalization
            const entityStoreModule = await import('../../../../stores/entityStore');
            const entityStore = entityStoreModule.default.getState();
            
            const newNote = await entityStore.actions.createEntity(newNoteEntityData);
            
            log.log('[notes_create] Note entity created via actions:', { 
                id: newNote._id, 
                name: newNote.name,
                llmId: newNote.llmId
            });
            
            // Handle folder relationship if folderId provided
            if (folderId) {
                try {
                    const folder = entityStore.entities.find((e: any) => e._id === folderId);
                    
                    if (folder && folder.entityType === 'Group') {
                        const newNoteId = newNote._id || newNote.id;
                        const currentChildEntities = folder.skeleton?.childEntities || [];
                        
                        console.log('[notes_create] 📁 Folder state BEFORE update:', {
                            folderId: folderId.slice(-8),
                            currentChildEntities: currentChildEntities.map((id: string) => id.slice(-8)),
                            addingNoteId: newNoteId.slice(-8)
                        });
                        
                        // Add note to folder's childEntities (action handles events automatically)
                        await entityStore.actions.updateEntity(folderId, {
                            skeleton: {
                                childEntities: [...currentChildEntities, newNoteId]
                            }
                        });
                        
                        log.log('[notes_create] ✅ Added note to folder:', { noteId: newNoteId, folderId });
                    } else {
                        log.warn('[notes_create] Folder not found or not a Group:', folderId);
                    }
                } catch (folderError) {
                    log.error('[notes_create] Failed to add note to folder:', folderError);
                    // Don't fail the whole operation - note is still created
                }
            }

            // ✅ Register llmId mapping for context usage
            const noteLlmId = newNote.llmId;
            const noteActualId = newNote._id || newNote.id;
            if (noteLlmId && noteActualId) {
                const { llmIdMapper } = await import('../../../../utils/llmIdMapper');
                llmIdMapper.registerMapping(noteLlmId, noteActualId, 'document', 'Note', newNote.name);
                log.log('[notes_create] ✅ Registered llmId mapping:', { noteLlmId, noteActualId });
            }
            
            // ✅ CRITICAL: Register with dataspace tree for BOTH folder AND root creation
            // This ensures the note appears in the correct position immediately
            // Without this, the note appears at the bottom and jumps when the edge syncs from backend
            if (noteActualId && spaceId) {
                try {
                    const parentId = folderId || spaceId;
                    const parentType = folderId ? 'folder' : 'space root';
                    
                    const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
                    const treeStore = useDataspaceTreeStore.getState();
                    const edgeKey = `${parentId}:${noteActualId}`;
                    
                    treeStore._applyAddNode(edgeKey, {
                        id: noteActualId,
                        parentId: parentId,
                        pos: "M",
                        expanded: false,
                        kind: 'canonical'
                    });
                    
                    log.log('[notes_create] ✅ Added to dataspace tree:', { parentId, parentType, noteId: noteActualId });
                } catch (treeError) {
                    log.warn('[notes_create] ⚠️ Failed to add to tree (non-fatal):', treeError);
                }
            }
            if (noteLlmId && noteActualId) {
                const { llmIdMapper } = await import('../../../../utils/llmIdMapper');
                llmIdMapper.registerMapping(noteLlmId, noteActualId, 'document', 'Note', newNote.name);
                log.log('[notes_create] ✅ Registered llmId mapping:', { noteLlmId, noteActualId });
            }
            
            // ✅ Note: No manual event emission needed! 
            // entityStore.actions.createEntity() already emitted the event
            // and updated all stores via the single mutation funnel
            
            let windowInfo = null;
            
            // Create window only if requested (default: true for backwards compatibility)
            if (createWindow !== false) {
                try {
                    // Use space_open_window command for proper multi-store coordination
                    const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
                    const { executeCommand } = useCommandRegistryStore.getState();
                    
                    const entityId = newNote._id || newNote.id;
                    const entityLlmId = newNote.llmId;
                    
                    console.log('[notes_create] Opening window for entity:', {
                        entityId,
                        entityLlmId,
                        entityType: newNote.entityType,
                        name: newNote.name
                    });
                    
                    const result = (await executeCommand('space_open_window', {
                      entityId
                    })) as import('../../../../types/standardToolResults').StandardToolResult;
                    
                    if (result.status === 'success') {
                        const windowId = result.data?.windowId;
                        log.log('[notes_create] ✅ Window opened successfully:', windowId);
                        
                        windowInfo = {
                            id: windowId,
                            title: title.trim(),
                            appType: 'notes-markdown',
                            entityId,
                            entityLlmId
                        };
                        
                        log.log('[notes_create] Window created for note:', windowInfo);
                    } else {
                        log.error('[notes_create] Failed to open window:', result.message || 'Unknown error');
                        throw new Error(result.message || 'Failed to open window');
                    }
                } catch (windowError) {
                    log.error('[notes_create] ❌ Failed to create window for note:', {
                        error: windowError,
                        errorMessage: windowError instanceof Error ? windowError.message : String(windowError),
                        noteId: newNote._id || newNote.id,
                        noteTitle: title
                    });
                }
            } else {
                log.log('[notes_create] Skipping window creation (createWindow=false)');
            }
            
            const result = {
                entityId: newNote._id || newNote.id, // ✅ STANDARDIZED: For sidebar rename extraction
                note: {
                    id: newNote.id || newNote._id,
                    llmId: (newNote as any).llmId,
                    title: title.trim(),
                    entityType: newNote.entityType || 'Note',
                    created_at: newNote.created_at,
                    icon: newNote.icon
                },
                window: windowInfo
            };
            
            log.log('[notes_create] Note created with llmId:', { 
                llmId: (newNote as any).llmId,
                entityType: newNote.entityType,
                hasLlmId: !!(newNote as any).llmId
            });
            
            log.log('[notes_create] ✅ Note created successfully:', result);
            
            return createSuccessResult(
                `Note created successfully: "${title.trim()}"${windowInfo ? ' (with window)' : ''} Note is ready for immediate editing.`,
                result,
                {
                    format: 'json',
                    metadata: {
                        executionTime: Date.now() - startTime,
                        method: 'self-contained',
                        affectedEntities: [newNote.id || newNote._id, ...(windowInfo ? [windowInfo.id] : [])],
                        noteTitle: title.trim()
                    } as any,
                    ui: {
                        refreshContext: true,
                        autoFocus: windowInfo?.id,
                        resultPreview: 'summary'
                    }
                }
            );
            
        } catch (error: any) {
            log.error('[notes_create] Error creating note:', error);
            return createErrorResult(
                'Note creation failed',
                `Failed to create note: ${error.message || 'Unknown error'}`,
                'execution',
                { 
                    data: { 
                        title,
                        spaceId,
                        errorStack: error instanceof Error ? error.stack : undefined
                    },
                    metadata: { executionTime: Date.now() - startTime }
                }
            );
        }
    }
};

// ✅ Register this command in the entity creation registry
// This allows EntitySidebar to discover it without hardcoding Note schemas
entityCreationRegistry.register({
    commandId: 'notes_create',
    entityType: 'Note',
    displayName: 'Note',
    icon: {
        type: 'phosphor',
        phosphorIcon: 'Note',
        phosphorWeight: 'duotone'
    },
    description: 'Create a markdown note',
    canCreateInFolder: true,
    openAfterCreate: true,
    category: 'note',
    sortOrder: 2, // After documents, before other types
    shortcut: '⌘⇧N'
});
